import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './OnboardingVideoCard.css';

const ONBOARDING_VIDEO_SRC = '/V10-Onboarding-Typix.mp4';

const VIDEO_PLAYLIST = [
  { src: '/V10-Onboarding-Typix.mp4', titleKey: 'Découvrez Typix Néo en 2 min' },
  { src: '/Agent-IA.mp4', titleKey: 'Agent IA' },
  { src: '/V1-Générateur-de-brief.mp4', titleKey: 'Générateur de brief' },
  { src: '/V1-Découverte-mots-clés.mp4', titleKey: 'Découverte de mots-clés' },
  { src: '/Redaction-IA.mp4', titleKey: 'Rédaction IA' },
  { src: '/Score-de-contenu.mp4', titleKey: 'Score de contenu' },
  { src: '/Création-de-projet.mp4', titleKey: 'Création de projet' },
  { src: '/V1-Rédaction-en-lot.mp4', titleKey: 'Rédaction en lot' },
];

const ONBOARDING_VIDEO_DISMISSED_KEY = 'typix-onboarding-video-dismissed';

function getPlaylistIndex(src) {
  const index = VIDEO_PLAYLIST.findIndex((item) => item.src === src);
  return index >= 0 ? index : 0;
}

export default function OnboardingVideoCard({
  hidden = false,
  videoSrc = ONBOARDING_VIDEO_SRC,
  titleKey = 'Découvrez Typix Néo en 2 min',
  compact = false,
  dismissible = true,
  enablePlaylist = true,
  className = '',
}) {
  const { t } = useTranslation();
  const previewSrc = videoSrc || ONBOARDING_VIDEO_SRC;
  const videoRef = useRef(null);
  const playlistMatch = VIDEO_PLAYLIST.find((item) => item.src === previewSrc);
  const displayTitleKey = playlistMatch?.titleKey || titleKey;

  const [showCard, setShowCard] = useState(() => {
    if (!dismissible) return true;
    try {
      return localStorage.getItem(ONBOARDING_VIDEO_DISMISSED_KEY) !== '1';
    } catch {
      return true;
    }
  });

  const [showModal, setShowModal] = useState(false);
  const [playlistIndex, setPlaylistIndex] = useState(() => getPlaylistIndex(previewSrc));
  const [showNextChoice, setShowNextChoice] = useState(false);

  useEffect(() => {
    setPlaylistIndex(getPlaylistIndex(previewSrc));
    setShowNextChoice(false);
  }, [previewSrc]);

  const currentVideo = enablePlaylist
    ? VIDEO_PLAYLIST[playlistIndex] || VIDEO_PLAYLIST[0]
    : { src: previewSrc, titleKey: displayTitleKey };
  const nextPlaylistIndex = (playlistIndex + 1) % VIDEO_PLAYLIST.length;
  const nextVideo = enablePlaylist ? VIDEO_PLAYLIST[nextPlaylistIndex] : null;

  const dismissCard = (e) => {
    e?.stopPropagation?.();
    setShowCard(false);
    try {
      localStorage.setItem(ONBOARDING_VIDEO_DISMISSED_KEY, '1');
    } catch {
      // ignore
    }
  };

  const openModal = () => {
    if (enablePlaylist) {
      setPlaylistIndex(getPlaylistIndex(previewSrc));
    }
    setShowNextChoice(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowNextChoice(false);
    setShowModal(false);
  };

  const playNextVideo = () => {
    if (!enablePlaylist) return;
    setShowNextChoice(false);
    setPlaylistIndex(nextPlaylistIndex);
  };

  const handleVideoEnded = () => {
    if (enablePlaylist) {
      setShowNextChoice(true);
    }
  };

  useEffect(() => {
    if (!showModal) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showModal]);

  useEffect(() => {
    if (!showModal || showNextChoice || !videoRef.current) return;
    const video = videoRef.current;
    video.load();
    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  }, [showModal, playlistIndex, showNextChoice, currentVideo.src]);

  if (hidden || !showCard) return null;

  const cardClassName = [
    'onboarding-video-card',
    compact ? 'onboarding-video-card--compact' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={cardClassName}>
        {dismissible && (
          <button
            type="button"
            className="onboarding-video-card__close"
            aria-label={t('Fermer')}
            onClick={dismissCard}
          >
            <CloseIcon style={{ fontSize: compact ? 12 : 14 }} />
          </button>
        )}

        <button
          type="button"
          className="onboarding-video-card__preview"
          onClick={openModal}
          aria-label={t('Regarder la vidéo')}
        >
          <video
            key={previewSrc}
            className="onboarding-video-card__thumb"
            src={previewSrc}
            muted
            preload="metadata"
            onLoadedMetadata={(e) => {
              try {
                e.currentTarget.currentTime = 0.5;
              } catch {
                // ignore
              }
            }}
          />

          <span className="onboarding-video-card__play" aria-hidden="true">
            <PlayArrowIcon style={{ fontSize: compact ? 18 : 22, color: '#fff' }} />
          </span>
        </button>

        <div className="onboarding-video-card__body">
          <p className="onboarding-video-card__title">{t(displayTitleKey)}</p>
          <button
            type="button"
            className="onboarding-video-card__cta"
            onClick={openModal}
          >
            <span>{t('Regarder la vidéo')}</span>
            <ArrowForwardIcon style={{ fontSize: compact ? 12 : 14 }} />
          </button>
        </div>
      </div>

      {showModal &&
        createPortal(
          <div
            className="onboarding-video-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t(currentVideo.titleKey)}
            onClick={closeModal}
          >
            <div
              className="onboarding-video-modal__content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="onboarding-video-modal__close"
                aria-label={t('Fermer')}
                onClick={closeModal}
              >
                <CloseIcon style={{ fontSize: 20 }} />
              </button>

              <video
                ref={videoRef}
                key={currentVideo.src}
                className="onboarding-video-modal__player"
                src={currentVideo.src}
                controls
                autoPlay
                playsInline
                onEnded={handleVideoEnded}
              >
                <source src={currentVideo.src} type="video/mp4" />
              </video>

              {showNextChoice && nextVideo && (
                <div className="onboarding-video-modal__choice">
                  <p className="onboarding-video-modal__choice-title">
                    {t('Vidéo terminée')}
                  </p>
                  <p className="onboarding-video-modal__choice-text">
                    {t('Souhaitez-vous regarder la vidéo suivante ?')}
                  </p>
                  <p className="onboarding-video-modal__choice-next">
                    {t(nextVideo.titleKey)}
                  </p>
                  <div className="onboarding-video-modal__choice-actions">
                    <button
                      type="button"
                      className="onboarding-video-modal__choice-btn onboarding-video-modal__choice-btn--secondary"
                      onClick={closeModal}
                    >
                      {t('Fermer')}
                    </button>
                    <button
                      type="button"
                      className="onboarding-video-modal__choice-btn onboarding-video-modal__choice-btn--primary"
                      onClick={playNextVideo}
                    >
                      {t('Vidéo suivante')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
